import React, { ReactElement, useCallback, memo, forwardRef, useEffect, useMemo } from 'react';
import { Box, MenuItem, TextField, Typography } from '@mui/material';
import { makeStyles } from '@mui/styles';
import { AlyceTheme, SelectFilter, IOptions, IProductDetails } from '@alycecom/ui';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers';
import { difference, keys } from 'ramda';
import classNames from 'classnames';

import ConfirmStepButton from '../../../../components/Shared/ConfirmStepButton';
import { buildDynamicValidationSchema, validationSchema } from '../../store/product.schemas';
import TextProductOptions from '../ProductOptions/TextProductOptions';
import TruncatedText from '../../../../components/TruncatedText/TruncatedText';

export interface IProductSubmitFormProps {
  onSubmit?: (options: string, optionsData?: Record<string, string>) => void;
  product?: IProductDetails;
  optionsData?: Record<string, string>;
  submitText: string;
  disabledContinue: boolean;
}

const useStyles = makeStyles<AlyceTheme>(({ palette, spacing, breakpoints }) => ({
  options: {
    marginTop: spacing(1),
    marginBottom: spacing(2),
    marginLeft: 'auto',
    marginRight: 'auto',
    width: '100%',
    textAlign: 'left',
  },
  fieldHint: {
    marginTop: spacing(1 / 2),
    color: palette.grey.main,
  },
  text: {
    wordBreak: 'break-word',
  },
  truncated: {
    maxHeight: 32,
  },
  nonTruncated: {
    [breakpoints.down('sm')]: {
      overflowY: 'scroll',
      maxHeight: 100,
    },
  },
  buttonWrapper: {
    marginTop: 0,
    marginBottom: 0,
  },
  button: {
    fontSize: 12,
  },
}));

const getFieldName = (item: IOptions) => `${item.name}`;

const getComboOptions = (options: IOptions[]) =>
  options.reduce<Record<string, unknown>>((acc, item) => {
    const name = getFieldName(item);
    acc[name] = '';
    return acc;
  }, {});

const getComboOptionsValue = (selectedOptions: Record<string, string>, optionsList: IOptions[] = []): string => {
  const valuesMap = Object.entries(selectedOptions).map(([optionName, optionValue]) => {
    const optionsItem = optionsList.find(item => getFieldName(item) === optionName);
    const selectedItem =
      optionsItem?.type === 'text' ? optionValue : (optionsItem?.options || []).find(item => item === optionValue);
    return selectedItem ?? '';
  });

  return valuesMap.join(', ');
};

const ProductSubmitForm = forwardRef(
  (
    { product, optionsData, submitText, disabledContinue, onSubmit = () => {} }: IProductSubmitFormProps,
    ref: React.Ref<HTMLDivElement>,
  ): ReactElement => {
    const classes = useStyles();

    const hasOptions = Boolean(product?.options || product?.optionsList?.length);
    const isComboOptions = Boolean(product?.optionsList?.length);
    const comboFields = useMemo(() => getComboOptions(product?.optionsList ?? []), [product]);

    const { errors, reset, handleSubmit, control } = useForm({
      mode: 'all',
      defaultValues: isComboOptions ? comboFields : { option: '' },
      resolver: yupResolver(isComboOptions ? buildDynamicValidationSchema(Object.keys(comboFields)) : validationSchema),
    });

    const handleSelectProduct = useCallback(
      data => {
        const options = isComboOptions ? getComboOptionsValue(data, product?.optionsList) : data.option;
        onSubmit(options, data);
      },
      [isComboOptions, onSubmit, product],
    );

    const handleSelectProductWithoutOptions = useCallback(() => onSubmit(''), [onSubmit]);

    useEffect(() => {
      if (hasOptions && !isComboOptions && optionsData?.option) {
        reset({
          option: optionsData?.option,
        });
      }

      if (
        hasOptions &&
        isComboOptions &&
        optionsData &&
        difference(keys(optionsData), keys(comboFields)).length === 0
      ) {
        reset(optionsData);
      }
    }, [reset, hasOptions, isComboOptions, optionsData, comboFields]);

    if (!hasOptions) {
      return (
        <ConfirmStepButton
          data-testid="customProductOptions"
          py={2}
          width={['100%', 496]}
          text={submitText}
          onClick={handleSelectProductWithoutOptions}
          disabled={disabledContinue}
          alwaysMobile
          type="button"
        />
      );
    }

    return (
      <form onSubmit={handleSubmit(handleSelectProduct)}>
        <ConfirmStepButton
          data-testid="customProductOptions"
          py={2}
          width={['100%', 496]}
          text={submitText}
          disabled={disabledContinue}
          alwaysMobile
          type="submit"
        >
          <div className={classes.options} ref={ref}>
            {isComboOptions && (
              <Box mb={2}>
                <Typography className="Body-Regular-Left-Static-Bold">Select option(s):</Typography>
              </Box>
            )}
            {isComboOptions ? (
              (product?.optionsList || []).map(item => {
                const name = getFieldName(item);
                return (
                  <Box key={item.id} mb={1}>
                    <Controller
                      control={control}
                      name={name}
                      render={({ onChange, value }) => {
                        const commonProps = {
                          name,
                          value,
                          label: item.name,
                          fullWidth: true,
                          error: !!errors[name],
                          helperText: errors[name] && errors[name]?.message,
                        };

                        if (item.type === 'text') {
                          return <TextField {...commonProps} variant="outlined" onChange={onChange} />;
                        }

                        return (
                          <SelectFilter
                            {...commonProps}
                            dataTestId={`ProductSubmitForm.OptionField.${name}`}
                            renderItems={() =>
                              (item.options || []).map(optionItem => (
                                <MenuItem
                                  key={optionItem}
                                  value={optionItem}
                                  onClick={() => {
                                    onChange(optionItem);
                                  }}
                                >
                                  {optionItem}
                                </MenuItem>
                              ))
                            }
                          />
                        );
                      }}
                    />
                    {item.description && (
                      <TruncatedText overrideClasses={classes} lines={1} lineHeight={10}>
                        <Typography className={classNames(classes.fieldHint, 'Body-Small-Inactive')}>
                          {item.description}
                        </Typography>
                      </TruncatedText>
                    )}
                  </Box>
                );
              })
            ) : (
              <Controller
                control={control}
                name="option"
                render={({ onChange, value }) => (
                  <TextProductOptions
                    value={value}
                    description={product?.options}
                    errors={errors}
                    onChange={onChange}
                  />
                )}
              />
            )}
          </div>
        </ConfirmStepButton>
      </form>
    );
  },
);

export default memo(ProductSubmitForm);
